import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import { useParticipantStore } from './stores/participant'
import RegisterView from './views/RegisterView.vue'
import LoginView from './views/LoginView.vue'
import DashboardView from './views/DashboardView.vue'
import SponsorDashboardView from './views/SponsorDashboardView.vue'
import LandingView from './views/LandingView.vue'

declare module 'vue-router' {
  interface RouteMeta {
    standalone?: boolean
    requiresAuth?: boolean
  }
}

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    name: 'landing',
    component: LandingView,
    meta: { standalone: true },
  },
  {
    path: '/sponsor',
    name: 'sponsor-info',
    component: () => import('./views/SponsorInfoView.vue'),
    meta: { standalone: true },
  },
  {
    path: '/participant',
    name: 'participant-info',
    component: () => import('./views/ParticipantInfoView.vue'),
    meta: { standalone: true },
  },
  {
    path: '/sponsor-view/:token',
    name: 'sponsor-dashboard',
    component: SponsorDashboardView,
    meta: { standalone: true }, // No auth required, standalone page
  },
  {
    path: '/qris-test',
    name: 'qris-test',
    component: () => import('./views/QrisTestView.vue'),
    meta: { standalone: true }, // Secret sandbox page bypassing auth
  },
  {
    path: '/events/:slug',
    redirect: (to) => `/events/${to.params.slug}/dashboard`,
  },
  {
    path: '/events/:slug/register',
    name: 'register',
    component: RegisterView,
  },
  {
    path: '/events/:slug/login',
    name: 'login',
    component: LoginView,
  },
  {
    path: '/events/:slug/dashboard',
    name: 'dashboard',
    component: DashboardView,
    meta: { requiresAuth: true },
  },
  {
    path: '/events/:slug/wallet',
    name: 'wallet',
    component: () => import('./views/WalletView.vue'),
    meta: { requiresAuth: true },
  },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
})

// Route guard gating secure participant views
router.beforeEach(async (to, _from, next) => {
  // Skip guard entirely for standalone pages (landing, sponsor-view)
  if (to.meta.standalone) {
    return next()
  }

  const store = useParticipantStore()
  const slug = to.params.slug as string

  // Ensure event context is loaded for the current route's slug
  if (slug && (!store.event || store.event.slug !== slug)) {
    await store.loadEventContext(slug)
  }

  // Attempt to restore profile from local storage token if present
  if (store.token && !store.registration) {
    await store.fetchSessionProfile()
  }

  const isAuth = store.isAuthenticated

  if (to.meta.requiresAuth && !isAuth) {
    next({ name: 'login', params: { slug } })
  } else if ((to.name === 'login' || to.name === 'register') && isAuth) {
    next({ name: 'dashboard', params: { slug } })
  } else {
    next()
  }
})

export default router
